<script>
import { ref, provide } from 'vue';
// composable
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import InlineHistory from './InlineHistory.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// stores and apis
import { mapGetters } from 'vuex';
import ContactsAPI from 'dashboard/api/contacts';
import { isMultiNumberInbox } from 'dashboard/helper/multiNumberHelper';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  components: {
    MessageList,
    InlineHistory,
    ReplyBox,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
  },
  mixins: [inboxMixin],
  setup() {
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);

    const keyboardEvents = {
      Escape: {
        action: () => {
          isPopOutReplyBox.value = false;
        },
      },
    };

    useKeyboardEvents(keyboardEvents);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      isPopOutReplyBox,
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      // Chat unificado multi-número (Victor 2026-05-23): ids de las
      // conversaciones HERMANAS del mismo contacto en la misma sucursal.
      siblingConvIds: [],
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
      currentAccount: 'getCurrentAccount',
    }),
    // Roles Fotógrafo (retoque_scope) y Video (fotografo_scope) NO pueden marcar
    // Resuelto (Victor 2026-06-10): si escriben, la conv queda abierta para que el
    // RESPONSABLE del cliente la cierre. Super admin sí puede.
    isFotoOrVideo() {
      if (this.currentAccount?.super_admin === true) return false;
      const fp = this.currentAccount?.feature_permissions || {};
      return fp.retoque_scope === true || fp.fotografo_scope === true;
    },
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    // Pending (lo usan los Salesbots) también debe poder marcarse como resuelto:
    // antes no salía ni "Resolver" ni "Reabrir" y la conversación quedaba sin
    // acción (caso Brianna · Victor 2026-06-06).
    isPendingConv() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.PENDING;
    },
    // "Marcar como resuelto" reubicado bajo el último mensaje (Victor
    // 2026-05-19, estilo Kommo). El botón viejo arriba-derecha quedó oculto
    // en ResolveAction; este link reusa su lógica vía emitter (incl. modal
    // de atributos requeridos).
    isResolvedConv() {
      return (
        this.currentChat?.status === wootConstants.STATUS_TYPE.RESOLVED
      );
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    // ¿La conversación vive en una sucursal con 2+ números WhatsApp? Si sí,
    // activamos el chat unificado (merge) + colores por número.
    isMultiNumber() {
      return isMultiNumberInbox(this.$store, this.inboxId);
    },
    // Conversaciones a fusionar: la actual + las hermanas descubiertas (mismo
    // contacto, misma sucursal). Lee de allConversations para mantenerse vivo
    // por websocket (ADD_MESSAGE indexa por conversation_id).
    mergeConversations() {
      const ids = new Set([this.currentChat.id, ...this.siblingConvIds]);
      return this.$store.getters.getAllConversations.filter(c =>
        ids.has(c.id)
      );
    },
    // Mensajes de la conversación activa (sin merge). Base para no-leídos/scroll.
    currentChatMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    getMessages() {
      if (!this.isMultiNumber) return this.currentChatMessages;
      // Merge: junta los mensajes de todas las conversaciones del contacto en
      // la sucursal, dedupe por id, ordena por hora. Cada mensaje conserva su
      // inbox_id → la burbuja lo colorea (azul/verde).
      const seen = new Set();
      const merged = [];
      this.mergeConversations.forEach(conv => {
        (conv.messages || []).forEach(m => {
          if (!seen.has(m.id)) {
            seen.add(m.id);
            merged.push(m);
          }
        });
      });
      merged.sort(
        (a, b) => a.created_at - b.created_at || a.id - b.id
      );
      return this.isAWhatsAppChannel
        ? filterDuplicateSourceMessages(merged)
        : merged;
    },
    readMessages() {
      return getReadMessages(
        this.currentChatMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.currentChatMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.discoverSiblingConversations();
      // Al abrir/cambiar de chat SIEMPRE mostrar el último mensaje (abajo),
      // tanto en PC como en celular (Victor 2026-06-02). Salvo cuando se navega
      // a un mensaje específico (búsqueda) vía messageId en la ruta.
      if (!this.$route.query.messageId) {
        this.$nextTick(() => this.forceScrollToBottom());
      }
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
    this.discoverSiblingConversations();
    // Prune viewers stale cada 5s (movido del antiguo ConversationViewersBanner).
    this.viewersPruneInterval = setInterval(() => {
      this.$store.dispatch('conversationViewers/prune');
    }, 5000);
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    if (this.viewersPruneInterval) {
      clearInterval(this.viewersPruneInterval);
      this.viewersPruneInterval = null;
    }
  },

  methods: {
    // Chat unificado multi-número: descubre las conversaciones del contacto en
    // la misma sucursal y precarga sus mensajes para fusionarlos en el timeline.
    async discoverSiblingConversations() {
      this.siblingConvIds = [];
      if (!this.isMultiNumber) return;
      const contactId = this.currentChat?.meta?.sender?.id;
      const branchId = this.inbox?.branch_id ?? null;
      if (!contactId || branchId == null) return;
      try {
        const { data } = await ContactsAPI.getConversations(contactId);
        const convs = data?.payload || data || [];
        const ids = convs
          .filter(cv => {
            const ib = this.$store.getters['inboxes/getInbox'](cv.inbox_id);
            return ib?.branch_id === branchId;
          })
          .map(cv => cv.id);
        this.siblingConvIds = ids;
        ids
          .filter(id => id !== this.currentChat.id)
          .forEach(id =>
            this.$store.dispatch('loadSiblingMessages', id)
          );
      } catch (error) {
        this.siblingConvIds = [];
      }
    },
    markResolvedFromFooter() {
      emitter.emit(CMD_RESOLVE_CONVERSATION);
    },
    reopenFromFooter() {
      emitter.emit(CMD_REOPEN_CONVERSATION);
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    async onScrollToMessage({ messageId = '' } = {}) {
      this.makeMessagesRead();
      await this.$nextTick();
      // Sin mensaje objetivo (abrir un chat normal, o tocar "escribir" en
      // celular): ir directo al fondo. NO entrar al bucle de cargar mensajes
      // viejos — eso metía historial por arriba y hacía que el chat "saltara
      // hacia arriba" al abrirlo en celular (Victor 2026-06-03).
      if (!messageId) {
        this.forceScrollToBottom();
        return;
      }
      let el = document.getElementById('message' + messageId);
      // Si el mensaje original NO está cargado (es más viejo que la ventana
      // cargada), carga mensajes anteriores en lote hasta encontrarlo, así el
      // salto + parpadeo funcionan aunque la respuesta sea a un mensaje viejo
      // (Victor 2026-06-02).
      let attempts = 0;
      while (!el && attempts < 30 && !this.currentChat.allMessagesLoaded) {
        const loaded = await this.loadOlderMessagesDirect();
        await this.$nextTick();
        el = document.getElementById('message' + messageId);
        attempts += 1;
        if (!loaded) break;
      }
      if (el) {
        this.isProgrammaticScroll = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.highlightMessage(el);
      } else {
        // Sin mensaje objetivo (ej. al enviar o al tocar escribir en celular):
        // ir al mensaje más reciente (fondo absoluto).
        this.forceScrollToBottom();
      }
    },
    // Carga el lote anterior de mensajes (sin el gate de scrollTop), para poder
    // alcanzar un mensaje viejo al saltar a una respuesta (Victor 2026-06-02).
    async loadOlderMessagesDirect() {
      if (this.isLoadingPrevious) return false;
      if (this.currentChat.dataFetched !== true) return false;
      if (!this.currentChat.messages?.length) return false;

      this.isLoadingPrevious = true;
      try {
        await this.$store.dispatch('fetchPreviousMessages', {
          conversationId: this.currentChat.id,
          before: this.currentChat.messages[0].id,
        });
        return true;
      } catch (error) {
        return false;
      } finally {
        this.isLoadingPrevious = false;
      }
    },
    // Resalta el mensaje al que se saltó (glow dorado ~2s · Victor 2026-06-02).
    highlightMessage(messageElement) {
      const bubble =
        messageElement.querySelector('.left-bubble, .right-bubble') ||
        messageElement;
      bubble.classList.remove('reply-jump-highlight');
      // fuerza reflow para reiniciar la animación si se toca varias veces
      void bubble.offsetWidth;
      bubble.classList.add('reply-jump-highlight');
      setTimeout(() => bubble.classList.remove('reply-jump-highlight'), 2200);
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      // Abrir un chat SIEMPRE muestra el último mensaje (abajo). Si se navega a
      // un mensaje puntual (messageId), eso lo maneja onScrollToMessage.
      this.$nextTick(() => {
        if (this.$route.query.messageId) {
          this.scrollToBottom();
        } else {
          this.forceScrollToBottom();
        }
      });
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    // Va al FONDO absoluto del chat (último mensaje) y lo reintenta varias veces
    // para cubrir imágenes/adjuntos que cargan async y cambian la altura — sin
    // esto a veces quedaba "muy arriba" (Victor 2026-06-02). Respeta si el
    // usuario scrollea hacia arriba manualmente mientras tanto.
    forceScrollToBottom() {
      const jump = () => {
        if (!this.conversationPanel) return;
        this.isProgrammaticScroll = true;
        this.conversationPanel.scrollTop = this.conversationPanel.scrollHeight;
      };
      // El primer salto es incondicional (abrir un chat siempre baja al final).
      this.hasUserScrolled = false;
      jump();
      this.$nextTick(jump);
      [80, 200, 450, 900, 1600].forEach(ms => {
        setTimeout(() => {
          if (this.conversationPanel && !this.hasUserScrolled) jump();
        }, ms);
      });
    },
    // El historial inline carga async y agrega altura ARRIBA; volvemos al fondo
    // (salvo que el usuario ya haya scrolleado hacia arriba para leerlo).
    onInlineHistoryLoaded() {
      this.$nextTick(this.forceScrollToBottom);
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <Banner
      v-if="!currentChat.can_reply"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <Banner
      v-else-if="hasDuplicateInstagramInbox"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
    />
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <!-- Historial inline (Victor 2026-06-03): mensajes de conversaciones
             anteriores del cliente, de corrido, arriba de la conversacion actual. -->
        <InlineHistory
          :conversation-id="currentChat.id"
          @loaded="onInlineHistoryLoaded"
        />
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div
      class="flex relative flex-col"
      :class="{
        'modal-mask': isPopOutReplyBox,
        'bg-n-surface-1': !isPopOutReplyBox,
      }"
    >
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <!-- Pastilla central "X está en este perfil" eliminada (Victor 2026-05-19):
           la presencia ahora se muestra como línea fina dentro del ReplyBox. -->
      <!-- "Marcar como resuelto" reubicado bajo el último mensaje (Victor
           2026-05-19, estilo Kommo · link discreto). El botón viejo
           arriba-derecha quedó oculto en ResolveAction. -->
      <div
        v-if="!isPopOutReplyBox && (isOpen || isPendingConv || isResolvedConv)"
        class="flex justify-end px-4 pt-1"
      >
        <button
          v-if="(isOpen || isPendingConv) && !isFotoOrVideo"
          type="button"
          title="Marca resuelto si ya terminaste con el cliente"
          class="text-xs font-semibold text-n-slate-10 hover:text-n-slate-12 cursor-pointer border-b border-transparent hover:border-n-slate-6 transition-colors"
          @click="markResolvedFromFooter"
        >
          Marcar como resuelto
        </button>
        <button
          v-else
          type="button"
          title="Vuelve a abrir esta conversación"
          class="text-xs font-semibold text-n-slate-10 hover:text-n-slate-12 cursor-pointer border-b border-transparent hover:border-n-slate-6 transition-colors"
          @click="reopenFromFooter"
        >
          Reabrir conversación
        </button>
      </div>
      <ReplyBox
        :pop-out-reply-box="isPopOutReplyBox"
        @update:pop-out-reply-box="isPopOutReplyBox = $event"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute ltr:left-auto rtl:right-auto bottom-1;
    }
  }
}
</style>

<!-- Glow dorado al saltar a un mensaje respondido (Victor 2026-06-02). Va en un
     bloque NO scoped porque la burbuja la renderiza otro componente
     (components-next/message/bubbles/Base.vue) y la clase se agrega por JS. -->
<style>
.reply-jump-highlight {
  animation: replyJumpGlow 1.8s ease-out;
  border-radius: 0.75rem;
}
@keyframes replyJumpGlow {
  0% {
    box-shadow: 0 0 0 0 rgba(201, 163, 90, 0);
  }
  12% {
    box-shadow:
      0 0 0 3px rgba(201, 163, 90, 0.55),
      0 0 24px rgba(201, 163, 90, 0.45);
  }
  40% {
    box-shadow:
      0 0 0 3px rgba(201, 163, 90, 0.35),
      0 0 18px rgba(201, 163, 90, 0.3);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(201, 163, 90, 0);
  }
}
</style>
